import { EntityState, PayloadAction, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { IMenuRecipeDetail, IMenus } from '../../api';
import { RecipeMenuDiary } from '../../api/models/recipe-menu-diary.models';
import { RecipeMenuHeaderPrivateUser } from '../../api/models/recipe-menu-header-private-users';
import { RecipeMenuHeader } from '../../api/models/recipe-menu-header.models';
import { RecipeMenuWorksheetDetail, RecipeMenuWorksheetHeader } from '../../api/models/recipe-menu-worksheet.models';
import { filterObjects, getSortComparer } from '../../helpers';
import { RootState } from '../store';

const menuDetailsAdapter = createEntityAdapter<IMenus>({
  selectId: (data: IMenus) => data._id,
});

const menuWorksheetsAdapter = createEntityAdapter<RecipeMenuWorksheetHeader>({
  selectId: (data) => data.RecipeMenuWorksheetHeaderId,
  sortComparer: (a, b) => a.MenuWorksheetName.localeCompare(b.MenuWorksheetName),
});

const menuWorkSheetDetailsAdapter = createEntityAdapter<RecipeMenuWorksheetDetail>({
  selectId: (data) => data.RecipeMenuWorksheetDetailId,
  sortComparer: (a, b) => a.RecipeName.localeCompare(b.RecipeName),
});

const menuHeadersAdapter = createEntityAdapter<RecipeMenuHeader>({
  selectId: (data: RecipeMenuHeader) => data.RecipeMenuHeaderId,
  sortComparer: getSortComparer((a) => a.MenuName),
});

const recipeMenuHeaderPrivateUserAdapter = createEntityAdapter<RecipeMenuHeaderPrivateUser>({
  selectId: (data) => data.RecipeMenuHeaderPrivateUserId,
});

const menuDiaryAdapter = createEntityAdapter<RecipeMenuDiary>({
  selectId: (data) => data.RecipeMenuDiaryId,
  sortComparer: (a, b) => new Date(b.ChangeDate).getTime() - new Date(a.ChangeDate).getTime(),
});

interface MenuState {
  gridParameters: { customerId: string } | undefined;

  isMenuHeadersLoading: boolean;
  menuHeaders: EntityState<RecipeMenuHeader>;

  MenuList: [];
  MenuDetails: EntityState<IMenus>;

  isRecipeMenuHeaderPrivateUsersLoading: boolean;
  recipeMenuHeaderPrivateUsers: EntityState<RecipeMenuHeaderPrivateUser>;

  isLoadingMenuDetails: false;
  selectedRecipeHeaderKeys: [];

  isMenuDiariesLoading: boolean;
  menuDiaries: EntityState<RecipeMenuDiary>;

  isLoadingWorkSheetList: boolean;
  WorkSheetList: EntityState<RecipeMenuWorksheetHeader>;

  WorkSheetDetails: EntityState<RecipeMenuWorksheetDetail>;
  isLoadingWorkSheetDetails: boolean;
}

const initialState: MenuState = {
  gridParameters: undefined,

  isMenuHeadersLoading: true,
  menuHeaders: menuHeadersAdapter.getInitialState(),
  MenuList: [],
  MenuDetails: menuDetailsAdapter.getInitialState(),

  isRecipeMenuHeaderPrivateUsersLoading: false,
  recipeMenuHeaderPrivateUsers: recipeMenuHeaderPrivateUserAdapter.getInitialState(),

  isLoadingMenuDetails: false,
  isMenuDiariesLoading: false,
  menuDiaries: menuDiaryAdapter.getInitialState(),
  selectedRecipeHeaderKeys: [],
  isLoadingWorkSheetList: false,
  WorkSheetList: menuWorksheetsAdapter.getInitialState(),
  WorkSheetDetails: menuWorkSheetDetailsAdapter.getInitialState(),
  isLoadingWorkSheetDetails: false,
};

export const menusSlice = createSlice({
  name: 'menus',
  initialState,
  reducers: {
    resetState: () => initialState,
    resetMenus: (state: MenuState) => {
      state.gridParameters = initialState.gridParameters;
      state.menuHeaders = initialState.menuHeaders;
    },
    setGridParameters: (state: MenuState, action: PayloadAction<{ customerId: string } | undefined>) => {
      state.gridParameters = action.payload;
    },
    resetGridParameters: (state: MenuState) => {
      state.gridParameters = initialState.gridParameters;
    },
    setIsLoadingMenuHeaders: (state: MenuState, action: PayloadAction<boolean>) => {
      state.isMenuHeadersLoading = action.payload;
    },
    setMenuHeader: (state: MenuState, action: PayloadAction<RecipeMenuHeader>) => {
      menuHeadersAdapter.setOne(state.menuHeaders, action.payload);
    },
    setMenuHeaders: (state: MenuState, action: PayloadAction<RecipeMenuHeader[]>) => {
      menuHeadersAdapter.setAll(state.menuHeaders, action.payload);
    },
    removeMenuHeader: (state: MenuState, action: PayloadAction<string>) => {
      menuHeadersAdapter.removeOne(state.menuHeaders, action.payload);
    },
    // TODO: Need redinement
    setSelectedRecipeHeader: (state, action: PayloadAction<string>) => {
      //state.selectedRecipeHeaderKeys = action.payload;
    },
    // TODO: Need redinement
    setAllSelectedRecipeHeaderKeys: (state) => {
      const entities = state.MenuDetails.entities;
      const menuKey = Object.keys(entities)[0];
      const recipes = entities[menuKey]?.recipes || [];

      const recipeHeaderIds = recipes.map((recipe: any) => recipe.recipeHeaderId);

      //state.selectedRecipeHeaderKeys = [...recipeHeaderIds] as string[];
    },
    //TODO: Need redinement
    setSelectedCategoryHeaderKeys: (state, action: { payload: string }) => {
      // console.log(state.MenuDetails);
      // const entities = state.MenuDetails.entities;
      // const menuKey = Object.keys(entities)[0];
      // const recipes = entities[menuKey]?.recipes || [];
      // console.log(entities, menuKey, recipes);
      // const categoryAssociatedRecipes = recipes.filter((recipe: any) => recipe.RecipeCategoryId === action.payload);
      // console.log(categoryAssociatedRecipes);
      // const recipeHeaderIds = categoryAssociatedRecipes.map((recipe: any) => recipe.recipeHeaderId);
      // console.log(recipeHeaderIds);
      // state.selectedRecipeHeaderKeys = [...state.selectedRecipeHeaderKeys, ...recipeHeaderIds] as string[];
    },

    setMenuDetails: (state: MenuState, action: PayloadAction<IMenus[]>) => {
      menuDetailsAdapter.setAll(state.MenuDetails, action.payload);
    },
    updateMenuDetails: (state, action: PayloadAction<Partial<IMenus> & { _id: string }>) => {
      menuDetailsAdapter.updateOne(state.MenuDetails, {
        id: action.payload._id,
        changes: action.payload,
      });
    },
    updateMenuCategoryIsCollapsed: (state, action: PayloadAction<{ isCollapsed: boolean; categoryId: string }>) => {
      const menus = Object.values(state.MenuDetails.entities) as IMenus[];

      menus.forEach((menu) => {
        if (menu?.recipes) {
          const updatedRecipes = menu.recipes.map((recipe: IMenuRecipeDetail) =>
            recipe.RecipeCategoryId === action.payload.categoryId
              ? { ...recipe, IsCollapsed: action.payload.isCollapsed }
              : recipe
          );

          menuDetailsAdapter.updateOne(state.MenuDetails, {
            id: menu._id,
            changes: { recipes: updatedRecipes },
          });
        }
      });
    },

    setIsRecipeMenuHeaderPrivateUsersLoading: (state: MenuState, action: PayloadAction<boolean>) => {
      state.isRecipeMenuHeaderPrivateUsersLoading = action.payload;
    },
    setRecipeMenuHeaderPrivateUsers: (state: MenuState, action: PayloadAction<RecipeMenuHeaderPrivateUser[]>) => {
      recipeMenuHeaderPrivateUserAdapter.setAll(state.recipeMenuHeaderPrivateUsers, action.payload);
    },

    setLoadingMenuDetails: (state, action: { payload: boolean }) => {
      //state.isLoadingMenuDetails = action.payload;
    },
    setLoadingWorkSheetList: (state, action: PayloadAction<boolean>) => {
      state.isLoadingWorkSheetList = action.payload;
    },
    setMenuWorkSheetList: (state, action: PayloadAction<RecipeMenuWorksheetHeader[]>) => {
      menuWorksheetsAdapter.setAll(state.WorkSheetList, action.payload);
    },
    createMenuWorkSheet: (state, action: PayloadAction<RecipeMenuWorksheetHeader>) => {
      menuWorksheetsAdapter.setOne(state.WorkSheetList, action.payload);
    },
    updateMenuWorkSheet: (state, action: PayloadAction<RecipeMenuWorksheetHeader>) => {
      menuWorksheetsAdapter.setOne(state.WorkSheetList, action.payload);
    },
    deleteMenuWorkSheet: (state, action: PayloadAction<string>) => {
      menuWorksheetsAdapter.removeOne(state.WorkSheetList, action.payload);
    },

    setMenuWorkSheetDetails: (state, action: PayloadAction<RecipeMenuWorksheetDetail[]>) => {
      menuWorkSheetDetailsAdapter.setAll(state.WorkSheetDetails, action.payload);
    },
    updateMenuWorkSheetDetail: (state, action: PayloadAction<RecipeMenuWorksheetDetail>) => {
      menuWorkSheetDetailsAdapter.setOne(state.WorkSheetDetails, action.payload);
    },
    setLoadingMenuWorkSheetDetails: (state, action: PayloadAction<boolean>) => {
      state.isLoadingWorkSheetDetails = action.payload;
    },
    setIsMenuDiariesLoading: (state: MenuState, action: PayloadAction<boolean>) => {
      state.isMenuDiariesLoading = action.payload;
    },
    setMenuDiaries: (state: MenuState, action: PayloadAction<RecipeMenuDiary[]>) => {
      menuDiaryAdapter.setAll(state.menuDiaries, action.payload);
    },
    removeMenuDiary: (state: MenuState, action: PayloadAction<string>) => {
      menuDiaryAdapter.removeOne(state.menuDiaries, action.payload);
    },
  },
});

export const { selectAll: selectAllMenuDetails, selectById: selectAllMenuDetailById } = menuDetailsAdapter.getSelectors<
  EntityState<IMenus>
>((state: EntityState<IMenus>) => state);

const getMenuDetails = (state: RootState): EntityState<IMenus> => state.menus.MenuDetails;

export const { selectAll: selectAllRecipeMenuHeaderPrivateUsers } = recipeMenuHeaderPrivateUserAdapter.getSelectors();

const getFilterText = (_: unknown, filterText: string | undefined) => filterText;

export const selectMenuDetailGridData = createSelector([getMenuDetails, getFilterText], (menuDetail, filterText) => {
  const collapsedCategoryIds = new Set<string | undefined>();

  // Extract recipes while asserting type
  const menurecipeDetails: IMenuRecipeDetail[] = selectAllMenuDetails(menuDetail).flatMap(
    (menu) => (menu.recipes as IMenuRecipeDetail[]) ?? []
  );

  const result = filterRecipeHeaders(menurecipeDetails, filterText).filter((d) => {
    let include = true;

    if (!collapsedCategoryIds.has(d.RecipeCategoryId)) {
      collapsedCategoryIds.add(d.RecipeCategoryId);
    } else if (d.IsCollapsed) {
      include = false;
    }

    return include;
  });

  return result;
});

const filterRecipeHeaders = (recipeHeaders: IMenuRecipeDetail[], filterText?: string): IMenuRecipeDetail[] => {
  return filterObjects(
    recipeHeaders,
    filterText || '',
    ['CategoryName', 'RecipeName', 'RecipeNumber', 'RecipeType', 'Tags'],
    [
      {
        field: 'RecipeNumber',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          !!(val && val.toLowerCase().includes(queryString.toLowerCase())),
      },
      {
        field: 'RecipeType',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          (val.toLowerCase() === '0' && 'recipes'.includes(queryString)) ||
          (val.toLowerCase() === '1' && 'sub-recipes'.includes(queryString)) ||
          (val.toLowerCase() === '2' && 'prep item'.includes(queryString)),
      },
      {
        field: 'Tags',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          !!(val && val.toLowerCase().includes(queryString.toLowerCase())),
      },
    ]
  );
};

//

export const { selectAll: selectAllMenuHeaders, selectById: selectMenuHeaderById } = menuHeadersAdapter.getSelectors<
  EntityState<RecipeMenuHeader>
>((state: EntityState<RecipeMenuHeader>) => state);

export const selectMenusGridData = createSelector([selectAllMenuHeaders, getFilterText], (menuHeaders, filterText) => {
  const result = filterMenuHeaders(menuHeaders, filterText);
  return result;
});

const filterMenuHeaders = (recipeHeaders: RecipeMenuHeader[], filterText?: string): RecipeMenuHeader[] => {
  return filterObjects(
    recipeHeaders,
    filterText || '',
    ['MenuName', 'IsPrivate', 'IsHidden', 'IsShared'],
    [
      {
        field: 'IsPrivate',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          val.toLowerCase() === 'true' && 'secret'.includes(queryString.toLowerCase()),
      },
      {
        field: 'IsHidden',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          val.toLowerCase() === 'true' && 'hidden'.includes(queryString.toLowerCase()),
      },
      {
        field: 'IsShared',
        type: 'custom',
        filterFunction: (val: string, queryString: string) =>
          val.toLowerCase() === 'true' && 'shared'.includes(queryString.toLowerCase()),
      },
    ]
  );
};

export const { selectAll: selectAllMenuDiaries } = menuDiaryAdapter.getSelectors(
  (state: EntityState<RecipeMenuDiary>) => state
);

const getMenuDiary = (state: RootState): EntityState<RecipeMenuDiary> => state.menus.menuDiaries;

const filterMenuDiary = (menuDiary: RecipeMenuDiary[], filterText?: string): RecipeMenuDiary[] => {
  return filterObjects(menuDiary, filterText || '', ['ActionType', 'ActionSummary', 'ChangeByUser']);
};

export const selectMenuDiary = createSelector([getMenuDiary, getFilterText], (menuDiary, filterText) => {
  return filterMenuDiary(selectAllMenuDiaries(menuDiary), filterText);
});

export const selectMenusRecentlyEdited = createSelector([selectAllMenuHeaders], (headers) => {
  const result = headers
    .filter((d) => d.IsEditable && d.CanViewDetails)
    .sort((a, b) => new Date(b.LastUpdatedDate).getTime() - new Date(a.LastUpdatedDate).getTime())
    .splice(0, 4);

  return result;
});

export const { selectById: selectMenuWorksheetById, selectAll: selectAllMenuWorksheets } =
  menuWorksheetsAdapter.getSelectors();

export const selectWorksheetsGridData = createSelector(
  [selectAllMenuWorksheets, getFilterText],
  (menuWorksheets, filterText) => {
    const result = filterWorksheetHeaders(menuWorksheets, filterText);
    return result;
  }
);

const filterWorksheetHeaders = (
  worksheetHeaders: RecipeMenuWorksheetHeader[],
  filterText?: string
): RecipeMenuWorksheetHeader[] => {
  return filterObjects(worksheetHeaders, filterText || '', ['MenuWorksheetName', 'Period']);
};

export const { selectById: selectMenuWorksheetDetailById, selectAll: selectAllMenuWorksheetDetails } =
  menuWorkSheetDetailsAdapter.getSelectors();
